import React, { useMemo, useState, useEffect, useCallback, memo } from "react";
import { useShallow } from "zustand/shallow";
import { useMapRouteStore } from "@/stores/mapRouteStore";

export type FloorSelectorProps = {
  floors?: number[]; // по умолчанию: -2..4 (без 0)
  visibleCount?: number; // сколько плиток видно разом
  startAtBottom?: boolean; // начинать с «нижнего окна»
  className?: string;
};

export const FloorSelector: React.FC<FloorSelectorProps> = memo(({
  floors = [1, 2, 3, 4, 5, 6],
  visibleCount = 4,
  startAtBottom = true,
  className = "",
}) => {
  const { currentFloor, direction, selectFloorManually, setCurrentFloor } = useMapRouteStore(
    useShallow((s) => ({
      currentFloor: s.currentFloor,
      direction: s.direction,
      selectFloorManually: s.selectFloorManually,
      setCurrentFloor: s.setCurrentFloor,
    })),
  );

  const sorted = useMemo(() => [...floors].sort((a, b) => b - a), [floors]);
  const maxStart = Math.max(0, sorted.length - visibleCount);

  const [start, setStart] = useState(() => (startAtBottom ? maxStart : 0));

  const [selectedFloor, setSelectedFloor] = useState<number>(() => {
    if (currentFloor != null && sorted.includes(currentFloor))
      return currentFloor;
    if (sorted.includes(1)) return 1;
    return sorted[0];
  });

  useEffect(() => {
    const newMax = Math.max(0, sorted.length - visibleCount);
    setStart((s) => Math.min(s, newMax));
    if (!sorted.includes(selectedFloor)) {
      setSelectedFloor(sorted[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sorted, visibleCount]);

  // Синхронизация с currentFloor из контекста (когда камера переключает этаж)
  useEffect(() => {
    if (currentFloor != null && sorted.includes(currentFloor)) {
      setSelectedFloor(currentFloor);
    }
  }, [currentFloor, sorted]);

  const canPageUp = start > 0;
  const canPageDown = start < maxStart;

  const pageUp = useCallback(() => setStart(0), []);
  const pageDown = useCallback(() => setStart(maxStart), [maxStart]);

  const pick = useCallback(
    (f: number) => {
      setSelectedFloor(f);
      // Если есть маршрут, используем комбинированный action
      if (direction) {
        selectFloorManually(f);
      } else {
        setCurrentFloor(f);
      }
    },
    [direction, selectFloorManually, setCurrentFloor]
  );

  const visible = sorted.slice(start, start + visibleCount);

  return (
    <div
      className={`fixed z-[100] toinline-flex flex-col w-11 bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden ${className}`}
      aria-label="Переключатель этажей"
    >
      {canPageUp && (
        <button
          onClick={pageUp}
          aria-label="Показать верхние этажи"
          className="flex items-center justify-center h-11 w-full hover:bg-gray-50 active:translate-y-px"
        >
          {/* стрелка вверх: svg */}
          <svg
            className="w-4 h-4 text-gray-500"
            viewBox="0 0 24 24"
            fill="none"
            aria-hidden
          >
            <path
              d="M6 14l6-6 6 6"
              stroke="currentColor"
              strokeWidth="1.6"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      )}

      {visible.map((f, idx) => {
        const active = f === selectedFloor;
        return (
          <button
            key={f}
            onClick={() => pick(f)}
            aria-pressed={active}
            className={`cursor-pointer flex items-center justify-center h-11 w-full text-sm font-medium transition-colors duration-150 ${
              active
                ? "bg-[#1a5fb4] text-white"
                : "bg-white text-gray-800 hover:bg-gray-50"
            } ${idx === 0 ? "" : "border-t border-gray-200"}`}
          >
            {f}
          </button>
        );
      })}

      {canPageDown && (
        <button
          onClick={pageDown}
          aria-label="Показать нижние этажи"
          className="flex items-center justify-center h-11 w-full hover:bg-gray-50 active:translate-y-px border-t border-gray-200"
        >
          {/* стрелка вниз: svg */}
          <svg
            className="w-4 h-4 text-gray-500"
            viewBox="0 0 24 24"
            fill="none"
            aria-hidden
          >
            <path
              d="M6 10l6 6 6-6"
              stroke="currentColor"
              strokeWidth="1.6"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      )}
    </div>
  );
});
